import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useUpdateOrgUserRole } from '@/services/user-management'
import { useUserStore } from '@/store/UserStore'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { RoleID } from '@/services/inference.gen'
import { toast } from 'react-hot-toast'
const ChangeUserRoleSchema = z.object({
  role: z.nativeEnum(RoleID),
})

type ChangeUserRoleFormValues = z.infer<typeof ChangeUserRoleSchema>

type ChangeUserRoleProps = {
  userId: string
  userEmail: string
  role: RoleID
  disabled?: boolean
}

export const ChangeUserRole = ({
  userId,
  userEmail,
  role: initialRole,
  disabled,
}: ChangeUserRoleProps) => {
  const myEmail = useUserStore((state) => state.user?.email)
  const isDisabled = myEmail === userEmail || disabled

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [pendingRole, setPendingRole] = useState<RoleID | null>(null)

  const form = useForm<ChangeUserRoleFormValues>({
    resolver: zodResolver(ChangeUserRoleSchema),
    defaultValues: {
      role: initialRole,
    },
  })

  const { mutate, isPending } = useUpdateOrgUserRole()

  const onRoleChange = (newRole: RoleID) => {
    const currentRole = form.getValues('role')
    if (newRole === currentRole) return

    setPendingRole(newRole)
    setIsConfirmOpen(true)
  }

  const onConfirmChangeRole = () => {
    if (!pendingRole) return
    mutate(
      { user_id: Number(userId), role_id: pendingRole },
      {
        onSuccess: () => {
          if (pendingRole) {
            form.setValue('role', pendingRole)
          }
          setPendingRole(null)
          setIsConfirmOpen(false)
        },
        onError: (error) => {
          setPendingRole(null)
          setIsConfirmOpen(false)

          toast.error(JSON.stringify(error))
        },
      }
    )
  }

  const onCancelChange = () => {
    setPendingRole(null)
    setIsConfirmOpen(false)
  }

  return (
    <div className="inline-flex h-9">
      <Select
        defaultValue={initialRole}
        onValueChange={onRoleChange}
        disabled={isDisabled || isPending}
        data-testid={`role-selector-${userId}`}
      >
        <SelectTrigger
          className={cn(
            'border-primary text-primary h-9 py-0',
            'flex items-center justify-between',
            'focus:ring-primary/20',
            'hover:bg-primary/5',
            isPending && 'opacity-50'
          )}
          data-testid={`role-selector-trigger-${userId}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={RoleID.Admin} data-testid="role-option-admin">
            Admin
          </SelectItem>
          <SelectItem value={RoleID.User} data-testid="role-option-user">
            User
          </SelectItem>
          <SelectItem value={RoleID.Manager} data-testid="role-option-manager">
            Manager
          </SelectItem>
        </SelectContent>
      </Select>

      <ConfirmationDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Change user role?"
        description={
          <>
            This action will change the role of user with email:{' '}
            <b className="text-foreground/90">{userEmail}</b> from{' '}
            <b className="text-foreground/90">{initialRole}</b> to{' '}
            <b className="text-foreground/90">{pendingRole}</b>.
          </>
        }
        buttons={{
          confirm: {
            label: isPending ? 'Changing role...' : 'Change role',
            isLoading: isPending,
          },
          cancel: {
            label: 'Cancel',
          },
        }}
        onConfirm={onConfirmChangeRole}
        onCancel={onCancelChange}
        data-testid={`change-role-dialog-${userId}`}
      />
    </div>
  )
}
